import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToRaceDay, subscribeToClubRaceDays, createRaceDay,
  updateRaceDayStatus, addOfficer, addCondition, setCourseSetup, addFleetStart, addDecision, addBroadcast, addFinishRecord,
} from '../lib/raceDayStore';
import { loadSeasons } from '../lib/racetimeStore';
import { loadFleetConfig } from '../lib/fleetConfigStore';
import { getPublishedBundles } from '../lib/firebase';
import { RACE_DAY_STATUS, OFFICER_ROLE, SEA_STATE, VISIBILITY, START_TYPE, DECISION_TYPE, BROADCAST_CHANNEL, FINISH_STATUS } from '../lib/raceDaySchema';
import { addMinutesToHHMM, DEFAULT_SEQUENCE_MINUTES, SEQUENCE_MINUTES_OPTIONS } from '../lib/racetimeStore';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

export function reportWriteError(action, err, context = {}) {
  console.error(`Failed to ${action}:`, { action, ...context }, err);
  const errorCode = err?.code ? `${err.code}: ` : '';
  const errorMessage = err?.message || String(err || 'Unknown error');
  window.dispatchEvent(new CustomEvent('race-day-write-error', {
    detail: `Unable to ${action}. Please try again.\n${errorCode}${errorMessage}`,
  }));
}

// ── Helpers ─────────────────────────────────────────────────────────

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(iso) {
  try {
    const [y, m, d] = iso.split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function daysFromNow(iso) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const [y, m, d] = iso.split('-');
  const target = new Date(Number(y), Number(m) - 1, Number(d));
  return Math.round((target - today) / 86400000);
}

// ── Hub Panel (detail view) ─────────────────────────────────────────

function HubPanel({ raceDay }) {
  const { user } = useAuth();
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const handleStatusChange = useCallback(async (newStatus) => {
    if (!user || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      await updateRaceDayStatus(raceDay.id, newStatus, user.uid);
    } catch (err) {
      reportWriteError('update race day status', err, { raceDayId: raceDay.id });
    } finally {
      setUpdatingStatus(false);
    }
  }, [raceDay?.id, user, updatingStatus]);

  // Collect IDs superseded by a newer correction entry — an entry with
  // `supersedes` pointing to an original means the original is stale.
  const officerSupersededIds = new Set((raceDay.officers || []).filter((o) => o.supersedes).map((o) => o.supersedes));
  const decisionSupersededIds = new Set((raceDay.decisions || []).filter((d) => d.supersedes).map((d) => d.supersedes));
  const conditionSupersededIds = new Set((raceDay.conditions || []).filter((c) => c.supersedes).map((c) => c.supersedes));
  const activeOfficers = (raceDay.officers || []).filter((o) => !officerSupersededIds.has(o.id));
  const activeDecisions = (raceDay.decisions || []).filter((d) => !decisionSupersededIds.has(d.id));
  const activeConditions = (raceDay.conditions || []).filter((c) => !conditionSupersededIds.has(c.id));
  const fleetStarts = raceDay.fleet_starts || [];
  const latestCondition = activeConditions.length > 0 ? activeConditions[activeConditions.length - 1] : null;

  return (
    <section className="rd-panel">
      <div className="rd-hub-header">
        <h2>Race Day Overview</h2>
        <span className="rd-hub-date">{formatDate(raceDay.date)}</span>
      </div>

      {/* Status transition buttons */}
      <div className="rd-hub-status-section">
        <h3>Status</h3>
        <div className="rd-hub-status-row">
          <StatusBadge status={raceDay.status} />
          <div className="rd-hub-status-actions">
            {raceDay.status === RACE_DAY_STATUS.SCHEDULED && (
              <button className="btn btn-sm btn-success" disabled={updatingStatus} onClick={() => handleStatusChange(RACE_DAY_STATUS.RACING)}>
                Start Racing
              </button>
            )}
            {raceDay.status === RACE_DAY_STATUS.RACING && (
              <>
                <button className="btn btn-sm" disabled={updatingStatus} onClick={() => handleStatusChange(RACE_DAY_STATUS.COMPLETED)}>
                  Complete
                </button>
                <button className="btn btn-sm btn-danger" disabled={updatingStatus} onClick={() => handleStatusChange(RACE_DAY_STATUS.ABANDONED)}>
                  Abandon
                </button>
              </>
            )}
            {raceDay.status === RACE_DAY_STATUS.SCHEDULED && (
              <button className="btn btn-sm btn-warning" disabled={updatingStatus} onClick={() => handleStatusChange(RACE_DAY_STATUS.POSTPONED)}>
                Postpone
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Quick stats grid */}
      <div className="rd-hub-stats">
        <div className="rd-hub-stat">
          <span className="rd-hub-stat-value">{activeOfficers.length}</span>
          <span className="rd-hub-stat-label">Officers</span>
        </div>
        <div className="rd-hub-stat">
          <span className="rd-hub-stat-value">{fleetStarts.length}</span>
          <span className="rd-hub-stat-label">Starts</span>
        </div>
        <div className="rd-hub-stat">
          <span className="rd-hub-stat-value">{activeDecisions.length}</span>
          <span className="rd-hub-stat-label">Decisions</span>
        </div>
        <div className="rd-hub-stat">
          <span className="rd-hub-stat-value">{(raceDay.finish_records || []).length}</span>
          <span className="rd-hub-stat-label">Finishes</span>
        </div>
      </div>

      {/* Latest conditions */}
      {latestCondition && (
        <div className="rd-hub-conditions">
          <h3>Latest Conditions</h3>
          <div className="rd-hub-conditions-grid">
            {latestCondition.wind_speed_kts != null && (
              <span>Wind: {latestCondition.wind_speed_kts} kts</span>
            )}
            {latestCondition.wind_direction_deg != null && (
              <span>Direction: {latestCondition.wind_direction_deg}&deg;</span>
            )}
            {latestCondition.sea_state && (
              <span>Sea: {latestCondition.sea_state}</span>
            )}
            {latestCondition.visibility && (
              <span>Visibility: {latestCondition.visibility}</span>
            )}
          </div>
          {latestCondition.notes && <p className="rd-hub-condition-notes">{latestCondition.notes}</p>}
        </div>
      )}

      {/* Recent decisions */}
      {activeDecisions.length > 0 && (
        <div className="rd-hub-decisions">
          <h3>Recent Decisions</h3>
          <ul className="rd-hub-decision-list">
            {activeDecisions.slice(-3).reverse().map((d) => (
              <li key={d.id}>
                <span className={`rd-hub-decision-type rd-hub-dtype-${d.type}`}>{d.type.replace('_', ' ')}</span>
                <span>{d.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Officers on duty */}
      {activeOfficers.length > 0 && (
        <div className="rd-hub-officers">
          <h3>Officers on Duty</h3>
          <div className="rd-hub-officer-list">
            {activeOfficers.map((o) => (
              <div key={o.id} className="rd-hub-officer">
                <span className="rd-hub-officer-role">{o.role.replace('_', ' ')}</span>
                <span className="rd-hub-officer-name">{o.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// ── Briefing & Setup Panel ───────────────────────────────────────────

const ROLE_LABELS = {
  [OFFICER_ROLE.PRO]: 'Principal Race Officer',
  [OFFICER_ROLE.OOD]: 'Officer of the Day',
  [OFFICER_ROLE.MARK_LAYER]: 'Mark Layer',
  [OFFICER_ROLE.SAFETY]: 'Safety',
  [OFFICER_ROLE.TIMER]: 'Timer',
  [OFFICER_ROLE.SCORER]: 'Scorer',
};

function OfficerForm({ raceDayId }) {
  const { user } = useAuth();
  const [role, setRole] = useState(OFFICER_ROLE.OOD);
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = useCallback(async () => {
    if (!name.trim() || !user || saving) return;
    setSaving(true);
    try {
      await addOfficer(raceDayId, {
        id: crypto.randomUUID(),
        role,
        name: name.trim(),
        ...(contact.trim() ? { contact: contact.trim() } : {}),
        assigned_at: new Date(),
        assigned_by: user.uid,
      }, user.uid);
      setName('');
      setContact('');
    } catch (err) {
      reportWriteError('add officer', err, { raceDayId });
    } finally {
      setSaving(false);
    }
  }, [raceDayId, role, name, contact, user, saving]);

  return (
    <div className="rd-brief-form">
      <h3>Add Officer</h3>
      <div className="rd-brief-row">
        <select value={role} onChange={(e) => setRole(e.target.value)} className="rd-brief-select">
          {Object.entries(ROLE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rd-brief-input"
        />
        <input
          type="text"
          placeholder="Contact (optional)"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          className="rd-brief-input rd-brief-input-sm"
        />
        <button className="btn btn-primary btn-sm" disabled={!name.trim() || saving} onClick={handleAdd}>
          {saving ? 'Adding...' : 'Add'}
        </button>
      </div>
    </div>
  );
}

function ConditionsForm({ raceDayId }) {
  const { user } = useAuth();
  const [windSpeed, setWindSpeed] = useState('');
  const [windDir, setWindDir] = useState('');
  const [seaState, setSeaState] = useState('');
  const [vis, setVis] = useState('');
  const [temp, setTemp] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = useCallback(async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      await addCondition(raceDayId, {
        id: crypto.randomUUID(),
        recorded_at: new Date(),
        recorded_by: user.uid,
        ...(windSpeed ? { wind_speed_kts: Number(windSpeed) } : {}),
        ...(windDir ? { wind_direction_deg: Number(windDir) } : {}),
        ...(seaState ? { sea_state: seaState } : {}),
        ...(vis ? { visibility: vis } : {}),
        ...(temp ? { temperature_c: Number(temp) } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }, user.uid);
      setWindSpeed(''); setWindDir(''); setSeaState(''); setVis(''); setTemp(''); setNotes('');
    } catch (err) {
      reportWriteError('save conditions', err, { raceDayId });
    } finally {
      setSaving(false);
    }
  }, [raceDayId, windSpeed, windDir, seaState, vis, temp, notes, user, saving]);

  return (
    <div className="rd-brief-form">
      <h3>Log Conditions</h3>
      <div className="rd-brief-grid">
        <label>
          Wind (kts)
          <input type="number" min="0" max="99" step="0.1" value={windSpeed} onChange={(e) => setWindSpeed(e.target.value)} className="rd-brief-input" />
        </label>
        <label>
          Direction (&deg;)
          <input type="number" min="0" max="360" step="1" value={windDir} onChange={(e) => setWindDir(e.target.value)} className="rd-brief-input" />
        </label>
        <label>
          Sea State
          <select value={seaState} onChange={(e) => setSeaState(e.target.value)} className="rd-brief-select">
            <option value="">--</option>
            {Object.values(SEA_STATE).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>
          Visibility
          <select value={vis} onChange={(e) => setVis(e.target.value)} className="rd-brief-select">
            <option value="">--</option>
            {Object.values(VISIBILITY).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <label>
          Temp (&deg;C)
          <input type="number" min="-20" max="50" step="0.5" value={temp} onChange={(e) => setTemp(e.target.value)} className="rd-brief-input" />
        </label>
      </div>
      <div className="rd-brief-notes-row">
        <input type="text" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="rd-brief-input rd-brief-input-wide" />
        <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleAdd}>
          {saving ? 'Logging...' : 'Log Conditions'}
        </button>
      </div>
    </div>
  );
}

function CourseCardPicker({ raceDay, clubId }) {
  const { user } = useAuth();
  const [bundles, setBundles] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPublishedBundles(clubId).then(setBundles).catch(() => setBundles([]));
  }, [clubId]);

  const currentBundle = bundles?.find((b) => b.id === selectedBundle);
  const courses = currentBundle?.cards?.flatMap((card) =>
    (card.courses || []).map((c) => ({ ...c, cardName: card.name || card.id }))
  ) || [];

  const handleSet = useCallback(async () => {
    if (!user || saving || !selectedCourse) return;
    setSaving(true);
    try {
      const course = courses.find((c) => c.id === selectedCourse);
      await setCourseSetup(raceDay.id, {
        course_card_id: selectedBundle,
        course_number: course?.number || course?.id || selectedCourse,
        marks: [],
        set_by: user.uid,
        set_at: new Date(),
      }, user.uid);
    } catch (err) {
      reportWriteError('set course', err, { raceDayId: raceDay.id, courseId: selectedCourse });
    } finally {
      setSaving(false);
    }
  }, [raceDay?.id, user, saving, selectedBundle, selectedCourse, courses]);

  if (bundles === null) return <div className="rd-brief-form"><h3>Course Card</h3><p>Loading course cards...</p></div>;

  return (
    <div className="rd-brief-form">
      <h3>Course Card</h3>
      {raceDay.course_setup && (
        <div className="rd-brief-current">
          Current: <strong>{raceDay.course_setup.course_number || 'Set'}</strong>
          {raceDay.course_setup.course_card_id && <span> (bundle: {raceDay.course_setup.course_card_id})</span>}
        </div>
      )}
      {bundles.length === 0 ? (
        <p className="rd-brief-empty">No published course bundles found for this club.</p>
      ) : (
        <div className="rd-brief-row">
          <select value={selectedBundle} onChange={(e) => { setSelectedBundle(e.target.value); setSelectedCourse(''); }} className="rd-brief-select">
            <option value="">Select bundle...</option>
            {bundles.map((b) => <option key={b.id} value={b.id}>{b.name || b.id}</option>)}
          </select>
          {courses.length > 0 && (
            <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} className="rd-brief-select">
              <option value="">Select course...</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.number || c.name || c.id} ({c.cardName})</option>)}
            </select>
          )}
          <button className="btn btn-primary btn-sm" disabled={!selectedCourse || saving} onClick={handleSet}>
            {saving ? 'Setting...' : 'Set Course'}
          </button>
        </div>
      )}
    </div>
  );
}

function StartTimeSection({ raceDay, clubId }) {
  const [fleetConfig, setFleetConfig] = useState(null);

  useEffect(() => {
    loadFleetConfig(clubId).then(setFleetConfig).catch(() => setFleetConfig({ fleets: [], classes: [] }));
  }, [clubId]);

  if (!fleetConfig) return <div className="rd-brief-form"><h3>Start Times</h3><p>Loading fleet config...</p></div>;

  const fleets = fleetConfig.fleets || [];
  const existingStarts = raceDay.fleet_starts || [];

  if (fleets.length === 0) {
    return (
      <div className="rd-brief-form">
        <h3>Start Times</h3>
        <p className="rd-brief-empty">No fleets configured. Set up fleets in Fleet Management first.</p>
      </div>
    );
  }

  return (
    <div className="rd-brief-form">
      <h3>Start Times</h3>
      <div className="rd-brief-start-grid">
        {fleets.map((fleet) => {
          const starts = existingStarts.filter((s) => s.fleet_id === fleet.id);
          return (
            <div key={fleet.id} className="rd-brief-fleet-card">
              <div className="rd-brief-fleet-name">{fleet.name || fleet.id}</div>
              {starts.length > 0 ? (
                starts.map((s) => (
                  <div key={s.id} className="rd-brief-start-info">
                    <span>Race {s.race_number} &middot; Seq {s.sequence}</span>
                    <span className="rd-brief-start-time">{s.scheduled_start}</span>
                    <StatusBadge status={s.start_type} />
                  </div>
                ))
              ) : (
                <div className="rd-brief-no-start">No start recorded yet</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BriefingPanel({ raceDay }) {
  const { clubId } = useParams();

  const briefingOfficerSupersededIds = new Set((raceDay.officers || []).filter((o) => o.supersedes).map((o) => o.supersedes));
  const briefingConditionSupersededIds = new Set((raceDay.conditions || []).filter((c) => c.supersedes).map((c) => c.supersedes));
  const activeOfficers = (raceDay.officers || []).filter((o) => !briefingOfficerSupersededIds.has(o.id));
  const activeConditions = (raceDay.conditions || []).filter((c) => !briefingConditionSupersededIds.has(c.id));

  return (
    <section className="rd-panel rd-briefing-panel">
      <h2>Briefing &amp; Setup</h2>

      {/* Current officers */}
      {activeOfficers.length > 0 && (
        <div className="rd-brief-current-list">
          <h4>Officers Assigned</h4>
          <div className="rd-hub-officer-list">
            {activeOfficers.map((o) => (
              <div key={o.id} className="rd-hub-officer">
                <span className="rd-hub-officer-role">{ROLE_LABELS[o.role] || o.role}</span>
                <span className="rd-hub-officer-name">{o.name}</span>
                {o.contact && <span className="rd-brief-officer-contact">{o.contact}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <OfficerForm raceDayId={raceDay.id} />

      {/* Current conditions log */}
      {activeConditions.length > 0 && (
        <div className="rd-brief-current-list">
          <h4>Conditions Log</h4>
          <div className="rd-brief-conditions-log">
            {activeConditions.map((c) => (
              <div key={c.id} className="rd-brief-condition-entry">
                <div className="rd-brief-condition-values">
                  {c.wind_speed_kts != null && <span>{c.wind_speed_kts} kts</span>}
                  {c.wind_direction_deg != null && <span>{c.wind_direction_deg}&deg;</span>}
                  {c.sea_state && <span>{c.sea_state}</span>}
                  {c.visibility && <span>vis: {c.visibility}</span>}
                  {c.temperature_c != null && <span>{c.temperature_c}&deg;C</span>}
                </div>
                {c.notes && <div className="rd-brief-condition-note">{c.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <ConditionsForm raceDayId={raceDay.id} />

      <CourseCardPicker raceDay={raceDay} clubId={clubId} />

      <StartTimeSection raceDay={raceDay} clubId={clubId} />
    </section>
  );
}

// ── Start Sequence Panel ─────────────────────────────────────────────

const START_SEQUENCE_MINUTES = [5, 4, 1, 0]; // warning, preparatory, one-minute, start

function useCountdown(targetTime) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!targetTime) { setRemaining(null); return; }
    const tick = () => {
      const diff = targetTime - Date.now();
      setRemaining(diff > 0 ? diff : 0);
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [targetTime]);

  return remaining;
}

function formatCountdown(ms) {
  if (ms == null) return '--:--';
  if (ms <= 0) return '00:00';
  const totalSec = Math.ceil(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function FleetStartCard({ fleet, starts, raceDayId, raceNumber, sequence }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [countdownTarget, setCountdownTarget] = useState(null);
  const [sequenceStep, setSequenceStep] = useState(-1); // -1=idle, 0=5min, 1=4min, 2=1min, 3=started
  const remaining = useCountdown(countdownTarget);
  const [postponeDialog, setPostponeDialog] = useState(null);

  // Find existing start for this fleet + race number
  const existingStart = starts.find(
    (s) => s.fleet_id === fleet.id && s.race_number === raceNumber && !s.supersedes
  );

  const startSequence = useCallback(() => {
    // Begin 5-minute countdown
    const target = Date.now() + 5 * 60 * 1000;
    setCountdownTarget(target);
    setSequenceStep(0);
  }, []);

  // Auto-advance sequence steps
  useEffect(() => {
    if (remaining === null || remaining > 0 || sequenceStep < 0) return;
    if (sequenceStep < 3) {
      // Move to next step
      const nextStep = sequenceStep + 1;
      const nextMinutes = START_SEQUENCE_MINUTES[nextStep];
      if (nextMinutes > 0) {
        setCountdownTarget(Date.now() + nextMinutes * 60 * 1000);
      } else {
        setCountdownTarget(null);
      }
      setSequenceStep(nextStep);
    }
  }, [remaining, sequenceStep]);

  const handleRecordStart = useCallback(async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      await addFleetStart(raceDayId, {
        id: crypto.randomUUID(),
        fleet_id: fleet.id,
        class_ids: fleet.class_ids || [],
        race_number: raceNumber,
        sequence,
        scheduled_start: fleet.scheduled_start || '',
        actual_start: new Date(),
        start_type: START_TYPE.NORMAL,
        recorded_by: user.uid,
        recorded_at: new Date(),
      }, user.uid);
      setSequenceStep(3);
      setCountdownTarget(null);
    } catch (err) {
      reportWriteError('record start', err, { raceDayId, fleetId: fleet.id, raceNumber });
    } finally {
      setSaving(false);
    }
  }, [raceDayId, fleet, raceNumber, sequence, user, saving]);

  const handleGeneralRecall = useCallback(async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      await addFleetStart(raceDayId, {
        id: crypto.randomUUID(),
        fleet_id: fleet.id,
        class_ids: fleet.class_ids || [],
        race_number: raceNumber,
        sequence,
        scheduled_start: fleet.scheduled_start || '',
        start_type: START_TYPE.GENERAL_RECALL,
        recorded_by: user.uid,
        recorded_at: new Date(),
        ...(existingStart ? { supersedes: existingStart.id } : {}),
      }, user.uid, existingStart);
      await addDecision(raceDayId, {
        id: crypto.randomUUID(),
        type: DECISION_TYPE.GENERAL_RECALL,
        description: `General recall for ${fleet.name || fleet.id} (Race ${raceNumber})`,
        made_at: new Date(),
        made_by: user.uid,
        flags: ['First Substitute'],
      }, user.uid);
      setSequenceStep(-1);
      setCountdownTarget(null);
    } catch (err) {
      reportWriteError('record recall', err, { raceDayId, fleetId: fleet.id, raceNumber });
    } finally {
      setSaving(false);
    }
  }, [raceDayId, fleet, raceNumber, sequence, user, saving, existingStart]);

  const openPostponeDialog = useCallback(() => {
    if (!user || saving) return;
    setPostponeDialog({
      reason: '',
      newWarning: '',
      sequenceMinutes: fleet.sequence_minutes ?? DEFAULT_SEQUENCE_MINUTES,
    });
  }, [user, saving, fleet]);

  const handleConfirmPostpone = useCallback(async () => {
    if (!user || saving || !postponeDialog) return;
    const { reason, newWarning, sequenceMinutes } = postponeDialog;
    if (!reason.trim()) return;
    // If RC entered a new warning time, derive T=0 start (warning + sequence)
    // and store that as scheduled_start. AP-lower semantics: shipped apps read
    // scheduled_start as the race start and restart their countdown.
    const hasNewTime = typeof newWarning === 'string' && /^\d{1,2}:\d{2}$/.test(newWarning.trim());
    const scheduledStart = hasNewTime
      ? addMinutesToHHMM(newWarning.trim(), Number(sequenceMinutes) || DEFAULT_SEQUENCE_MINUTES)
      : (existingStart?.scheduled_start || '');
    setSaving(true);
    try {
      await addFleetStart(raceDayId, {
        id: crypto.randomUUID(),
        fleet_id: fleet.id,
        class_ids: fleet.class_ids || [],
        race_number: raceNumber,
        sequence,
        scheduled_start: scheduledStart,
        ...(hasNewTime ? {
          warning_time: newWarning.trim(),
          sequence_minutes: Number(sequenceMinutes) || DEFAULT_SEQUENCE_MINUTES,
        } : {}),
        start_type: START_TYPE.POSTPONED,
        notes: reason.trim(),
        recorded_by: user.uid,
        recorded_at: new Date(),
        ...(existingStart ? { supersedes: existingStart.id } : {}),
      }, user.uid, existingStart);
      const desc = hasNewTime
        ? `Postponed: ${fleet.name || fleet.id} (Race ${raceNumber}) — restart at warning ${newWarning.trim()} (start ${scheduledStart}, ${sequenceMinutes}-min sequence) — ${reason.trim()}`
        : `Postponed: ${fleet.name || fleet.id} (Race ${raceNumber}) — ${reason.trim()}`;
      await addDecision(raceDayId, {
        id: crypto.randomUUID(),
        type: DECISION_TYPE.POSTPONE,
        description: desc,
        made_at: new Date(),
        made_by: user.uid,
        flags: ['AP'],
      }, user.uid);
      setSequenceStep(-1);
      setCountdownTarget(null);
      setPostponeDialog(null);
    } catch (err) {
      reportWriteError('postpone race', err, { raceDayId, fleetId: fleet.id, raceNumber });
    } finally {
      setSaving(false);
    }
  }, [raceDayId, fleet, raceNumber, sequence, user, saving, existingStart, postponeDialog]);

  const handleAbandon = useCallback(async () => {
    if (!user || saving) return;
    const reason = prompt('Abandonment reason:');
    if (!reason) return;
    setSaving(true);
    try {
      await addFleetStart(raceDayId, {
        id: crypto.randomUUID(),
        fleet_id: fleet.id,
        class_ids: fleet.class_ids || [],
        race_number: raceNumber,
        sequence,
        scheduled_start: fleet.scheduled_start || '',
        start_type: START_TYPE.ABANDONED,
        notes: reason,
        recorded_by: user.uid,
        recorded_at: new Date(),
        ...(existingStart ? { supersedes: existingStart.id } : {}),
      }, user.uid, existingStart);
      await addDecision(raceDayId, {
        id: crypto.randomUUID(),
        type: DECISION_TYPE.ABANDON,
        description: `Abandoned: ${fleet.name || fleet.id} (Race ${raceNumber}) — ${reason}`,
        made_at: new Date(),
        made_by: user.uid,
      }, user.uid);
      setSequenceStep(-1);
      setCountdownTarget(null);
    } catch (err) {
      reportWriteError('abandon race', err, { raceDayId, fleetId: fleet.id, raceNumber });
    } finally {
      setSaving(false);
    }
  }, [raceDayId, fleet, raceNumber, sequence, user, saving, existingStart]);

  const stepLabels = ['Warning (5 min)', 'Preparatory (4 min)', 'One Minute', 'STARTED'];
  const isStarted = existingStart?.start_type === START_TYPE.NORMAL && existingStart?.actual_start;
  const isRecalled = existingStart?.start_type === START_TYPE.GENERAL_RECALL;
  const isPostponed = existingStart?.start_type === START_TYPE.POSTPONED;
  const isAbandoned = existingStart?.start_type === START_TYPE.ABANDONED;

  return (
    <div className={`rd-start-card${isStarted ? ' rd-start-card-started' : ''}${isRecalled ? ' rd-start-card-recalled' : ''}${isPostponed ? ' rd-start-card-postponed' : ''}${isAbandoned ? ' rd-start-card-abandoned' : ''}`}>
      <div className="rd-start-card-header">
        <div>
          <strong>{fleet.name || fleet.id}</strong>
          <span className="rd-start-race-num">Race {raceNumber}</span>
        </div>
        {existingStart && <StatusBadge status={existingStart.start_type} />}
      </div>

      {/* Countdown display */}
      {sequenceStep >= 0 && sequenceStep < 3 && (
        <div className="rd-start-countdown">
          <div className="rd-start-countdown-time">{formatCountdown(remaining)}</div>
          <div className="rd-start-countdown-label">{stepLabels[sequenceStep]}</div>
          <div className="rd-start-sequence-dots">
            {START_SEQUENCE_MINUTES.map((_, i) => (
              <span key={i} className={`rd-start-dot${i <= sequenceStep ? ' rd-start-dot-active' : ''}`} />
            ))}
          </div>
        </div>
      )}

      {isStarted && (
        <div className="rd-start-confirmed">
          Started at {existingStart.actual_start?.toDate?.()
            ? existingStart.actual_start.toDate().toLocaleTimeString()
            : 'recorded'}
        </div>
      )}

      {existingStart?.notes && (
        <div className="rd-start-notes">{existingStart.notes}</div>
      )}

      {/* Action buttons */}
      <div className="rd-start-actions">
        {!isStarted && !isAbandoned && sequenceStep < 0 && (
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={startSequence}>
            Begin Sequence
          </button>
        )}
        {!isStarted && !isAbandoned && sequenceStep >= 0 && sequenceStep < 3 && (
          <button className="btn btn-success btn-sm" disabled={saving} onClick={handleRecordStart}>
            Record Start
          </button>
        )}
        {!isStarted && !isAbandoned && (sequenceStep >= 0 || existingStart) && (
          <>
            <button className="btn btn-sm btn-danger" disabled={saving} onClick={handleGeneralRecall} title="General Recall (R)">
              Recall
            </button>
            <button className="btn btn-sm btn-warning" disabled={saving} onClick={openPostponeDialog} title="Postpone (P)">
              Postpone
            </button>
          </>
        )}
        {!isStarted && !isAbandoned && !isPostponed && (
          <button className="btn btn-sm" disabled={saving} onClick={handleAbandon} title="Abandon">
            Abandon
          </button>
        )}
      </div>
      {postponeDialog && (
        <PostponeDialog
          fleet={fleet}
          raceNumber={raceNumber}
          value={postponeDialog}
          saving={saving}
          onChange={setPostponeDialog}
          onCancel={() => setPostponeDialog(null)}
          onConfirm={handleConfirmPostpone}
        />
      )}
    </div>
  );
}

function PostponeDialog({ fleet, raceNumber, value, saving, onChange, onCancel, onConfirm }) {
  const { reason, newWarning, sequenceMinutes } = value;
  const hasNewTime = /^\d{1,2}:\d{2}$/.test((newWarning || '').trim());
  const seq = Number(sequenceMinutes) || DEFAULT_SEQUENCE_MINUTES;
  const derivedStart = hasNewTime ? addMinutesToHHMM(newWarning.trim(), seq) : null;
  return (
    <div className="rd-modal-backdrop" onClick={onCancel}>
      <div className="rd-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Postpone — {fleet.name || fleet.id} (Race {raceNumber})</h3>
        <p className="rd-modal-hint">
          AP hoisted = postpone indefinitely (leave warning empty).<br />
          AP lowered = enter the new warning signal time to restart the start sequence.
        </p>
        <label className="rd-modal-field">
          <span>Reason</span>
          <textarea
            rows={2}
            autoFocus
            value={reason}
            onChange={(e) => onChange({ ...value, reason: e.target.value })}
            placeholder="e.g. wind shift, course reset"
          />
        </label>
        <label className="rd-modal-field">
          <span>New warning signal (optional)</span>
          <input
            type="time"
            value={newWarning}
            onChange={(e) => onChange({ ...value, newWarning: e.target.value })}
          />
        </label>
        <label className="rd-modal-field">
          <span>Sequence</span>
          <select
            value={String(seq)}
            onChange={(e) => onChange({ ...value, sequenceMinutes: Number(e.target.value) })}
          >
            {SEQUENCE_MINUTES_OPTIONS.map((n) => (
              <option key={n} value={n}>{n} min</option>
            ))}
          </select>
        </label>
        {hasNewTime && (
          <p className="rd-modal-derived">
            Warning {newWarning.trim()} → Start <strong>{derivedStart}</strong> ({seq}-min sequence)
          </p>
        )}
        <div className="rd-modal-actions">
          <button className="btn btn-sm" disabled={saving} onClick={onCancel}>Cancel</button>
          <button
            className="btn btn-sm btn-warning"
            disabled={saving || !reason.trim()}
            onClick={onConfirm}
          >
            {hasNewTime ? 'Postpone & restart at new warning' : 'Postpone indefinitely'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StartsPanel({ raceDay }) {
  const { clubId } = useParams();
  const [fleetConfig, setFleetConfig] = useState(null);
  const [raceNumber, setRaceNumber] = useState(1);
  const panelRef = useRef(null);

  useEffect(() => {
    loadFleetConfig(clubId).then(setFleetConfig).catch(() => setFleetConfig({ fleets: [] }));
  }, [clubId]);

  const fleets = fleetConfig?.fleets || [];
  const starts = raceDay.fleet_starts || [];

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      // Shortcuts handled at card level — this is a placeholder for panel-level shortcuts
    };
    const el = panelRef.current;
    if (el) el.addEventListener('keydown', handler);
    return () => { if (el) el.removeEventListener('keydown', handler); };
  }, []);

  if (!fleetConfig) return <section className="rd-panel"><h2>Starts</h2><p>Loading fleet config...</p></section>;

  if (fleets.length === 0) {
    return (
      <section className="rd-panel">
        <h2>Start Sequence</h2>
        <p className="rd-brief-empty">No fleets configured. Set up fleets in Fleet Management first.</p>
      </section>
    );
  }

  // Determine max race number from existing starts
  const maxRace = starts.reduce((max, s) => Math.max(max, s.race_number || 1), 1);

  return (
    <section className="rd-panel" ref={panelRef} tabIndex={-1}>
      <div className="rd-starts-header">
        <h2>Start Sequence</h2>
        <div className="rd-starts-race-picker">
          <label>Race:</label>
          <select value={raceNumber} onChange={(e) => setRaceNumber(Number(e.target.value))}>
            {Array.from({ length: Math.max(maxRace, raceNumber) + 1 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>Race {n}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="rd-starts-hint">
        <kbd>Space</kbd> Begin/Record Start &middot; <kbd>R</kbd> Recall &middot; <kbd>P</kbd> Postpone
      </div>

      <div className="rd-starts-grid">
        {fleets.map((fleet, idx) => (
          <FleetStartCard
            key={fleet.id}
            fleet={fleet}
            starts={starts}
            raceDayId={raceDay.id}
            raceNumber={raceNumber}
            sequence={idx + 1}
          />
        ))}
      </div>

      {/* Recent decisions related to starts */}
      {(raceDay.decisions || []).filter(
        (d) => !d.supersedes && [DECISION_TYPE.GENERAL_RECALL, DECISION_TYPE.POSTPONE, DECISION_TYPE.ABANDON].includes(d.type)
      ).length > 0 && (
        <div className="rd-starts-decisions">
          <h3>Start Decisions</h3>
          <ul className="rd-hub-decision-list">
            {(raceDay.decisions || [])
              .filter((d) => !d.supersedes && [DECISION_TYPE.GENERAL_RECALL, DECISION_TYPE.POSTPONE, DECISION_TYPE.ABANDON].includes(d.type))
              .slice(-5).reverse()
              .map((d) => (
                <li key={d.id}>
                  <span className={`rd-hub-decision-type rd-hub-dtype-${d.type}`}>{d.type.replace(/_/g, ' ')}</span>
                  <span>{d.description}</span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ── Course Board Panel ───────────────────────────────────────────────

const CHANNEL_LABELS = {
  [BROADCAST_CHANNEL.VHF]: 'VHF',
  [BROADCAST_CHANNEL.FLAG]: 'Flag',
  [BROADCAST_CHANNEL.SOUND]: 'Sound',
  [BROADCAST_CHANNEL.APP]: 'App',
};

function CourseDisplay({ courseSetup }) {
  if (!courseSetup) {
    return (
      <div className="rd-course-display rd-course-empty">
        <p>No course set. Use Briefing tab to select a course card.</p>
      </div>
    );
  }

  const marks = courseSetup.marks || [];

  return (
    <div className="rd-course-display">
      <div className="rd-course-info-row">
        {courseSetup.course_number && (
          <div className="rd-course-number">Course {courseSetup.course_number}</div>
        )}
        {courseSetup.distance_nm != null && (
          <span className="rd-course-detail">{courseSetup.distance_nm} nm</span>
        )}
        {courseSetup.laps != null && (
          <span className="rd-course-detail">{courseSetup.laps} lap{courseSetup.laps !== 1 ? 's' : ''}</span>
        )}
        {courseSetup.wind_direction_deg != null && (
          <span className="rd-course-detail">Wind {courseSetup.wind_direction_deg}&deg;</span>
        )}
      </div>
      {marks.length > 0 ? (
        <div className="rd-course-marks">
          {marks.map((m, i) => (
            <div key={m.mark_id || i} className="rd-course-mark">
              <span className="rd-course-mark-num">{i + 1}</span>
              <span className="rd-course-mark-name">{m.name}</span>
              {m.lat != null && m.lng != null && (
                <span className="rd-course-mark-pos">
                  {m.lat.toFixed(4)}, {m.lng.toFixed(4)}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="rd-course-no-marks">No mark positions recorded.</p>
      )}
    </div>
  );
}

function CourseChangeForm({ raceDay, clubId }) {
  const { user } = useAuth();
  const [bundles, setBundles] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPublishedBundles(clubId).then(setBundles).catch(() => setBundles([]));
  }, [clubId]);

  const currentBundle = bundles?.find((b) => b.id === selectedBundle);
  const courses = currentBundle?.cards?.flatMap((card) =>
    (card.courses || []).map((c) => ({ ...c, cardName: card.name || card.id }))
  ) || [];

  const handleChange = useCallback(async () => {
    if (!user || saving || !selectedCourse) return;
    setSaving(true);
    try {
      const course = courses.find((c) => c.id === selectedCourse);
      await setCourseSetup(raceDay.id, {
        course_card_id: selectedBundle,
        course_number: course?.number || course?.id || selectedCourse,
        marks: [],
        set_by: user.uid,
        set_at: new Date(),
      }, user.uid);
      await addDecision(raceDay.id, {
        id: crypto.randomUUID(),
        type: DECISION_TYPE.COURSE_CHANGE,
        description: `Course changed to ${course?.number || course?.id || selectedCourse}`,
        made_at: new Date(),
        made_by: user.uid,
      }, user.uid);
      setSelectedBundle('');
      setSelectedCourse('');
    } catch (err) {
      reportWriteError('change course', err, { raceDayId: raceDay.id, courseId: selectedCourse });
    } finally {
      setSaving(false);
    }
  }, [raceDay?.id, user, saving, selectedBundle, selectedCourse, courses]);

  if (bundles === null) return <p>Loading course cards...</p>;
  if (bundles.length === 0) return <p className="rd-course-empty-hint">No published course bundles.</p>;

  return (
    <div className="rd-course-change">
      <h3>Change Course</h3>
      <div className="rd-course-change-row">
        <select value={selectedBundle} onChange={(e) => { setSelectedBundle(e.target.value); setSelectedCourse(''); }} className="rd-course-select">
          <option value="">Select bundle...</option>
          {bundles.map((b) => <option key={b.id} value={b.id}>{b.name || b.id}</option>)}
        </select>
        {courses.length > 0 && (
          <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} className="rd-course-select">
            <option value="">Select course...</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.number || c.name || c.id} ({c.cardName})</option>)}
          </select>
        )}
        <button className="btn btn-primary btn-sm" disabled={!selectedCourse || saving} onClick={handleChange}>
          {saving ? 'Changing...' : 'Change Course'}
        </button>
      </div>
    </div>
  );
}

function BroadcastForm({ raceDayId }) {
  const { user } = useAuth();
  const [channel, setChannel] = useState(BROADCAST_CHANNEL.VHF);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSend = useCallback(async () => {
    if (!user || saving || !message.trim()) return;
    setSaving(true);
    try {
      await addBroadcast(raceDayId, {
        id: crypto.randomUUID(),
        channel,
        message: message.trim(),
        sent_at: new Date(),
        sent_by: user.uid,
      }, user.uid);
      setMessage('');
    } catch (err) {
      reportWriteError('log broadcast', err, { raceDayId });
    } finally {
      setSaving(false);
    }
  }, [raceDayId, channel, message, user, saving]);

  return (
    <div className="rd-broadcast-form">
      <h3>Shown in the app (version 1.3.9 and later)</h3>
      <div className="rd-broadcast-row">
        <select value={channel} onChange={(e) => setChannel(e.target.value)} className="rd-course-select">
          {Object.entries(CHANNEL_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Message shown in the app..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="rd-broadcast-input"
          onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
        />
        <button className="btn btn-primary btn-sm" disabled={!message.trim() || saving} onClick={handleSend}>
          {saving ? 'Logging...' : 'Log'}
        </button>
      </div>
    </div>
  );
}

function FlagSignalForm({ raceDayId }) {
  const { user } = useAuth();
  const [flag, setFlag] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleLog = useCallback(async () => {
    if (!user || saving || !flag.trim()) return;
    setSaving(true);
    try {
      await addDecision(raceDayId, {
        id: crypto.randomUUID(),
        type: DECISION_TYPE.FLAG_SIGNAL,
        description: description.trim() || `Flag signal: ${flag.trim()}`,
        made_at: new Date(),
        made_by: user.uid,
        flags: [flag.trim()],
      }, user.uid);
      setFlag('');
      setDescription('');
    } catch (err) {
      reportWriteError('log flag signal', err, { raceDayId });
    } finally {
      setSaving(false);
    }
  }, [raceDayId, flag, description, user, saving]);

  return (
    <div className="rd-flag-form">
      <h3>Log Flag Signal</h3>
      <div className="rd-broadcast-row">
        <input
          type="text"
          placeholder="Flag (e.g. AP, N, X)"
          value={flag}
          onChange={(e) => setFlag(e.target.value)}
          className="rd-flag-input"
        />
        <input
          type="text"
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="rd-broadcast-input"
        />
        <button className="btn btn-primary btn-sm" disabled={!flag.trim() || saving} onClick={handleLog}>
          {saving ? 'Logging...' : 'Log'}
        </button>
      </div>
    </div>
  );
}

function CoursePanel({ raceDay }) {
  const { clubId } = useParams();

  const broadcasts = (raceDay.broadcasts || []).filter((b) => !b.supersedes);
  const flagDecisions = (raceDay.decisions || []).filter(
    (d) => !d.supersedes && [DECISION_TYPE.FLAG_SIGNAL, DECISION_TYPE.COURSE_CHANGE].includes(d.type)
  );

  return (
    <section className="rd-panel rd-course-panel">
      <h2>Course Board</h2>

      {/* Current course display — large, readable */}
      <CourseDisplay courseSetup={raceDay.course_setup} />

      {/* Course change mid-race */}
      <CourseChangeForm raceDay={raceDay} clubId={clubId} />

      {/* VHF / Broadcast log */}
      <BroadcastForm raceDayId={raceDay.id} />

      {broadcasts.length > 0 && (
        <div className="rd-broadcast-log">
          <h3>Broadcast Log</h3>
          <div className="rd-broadcast-entries">
            {[...broadcasts].reverse().map((b) => (
              <div key={b.id} className="rd-broadcast-entry">
                <span className={`rd-broadcast-channel rd-channel-${b.channel}`}>
                  {CHANNEL_LABELS[b.channel] || b.channel}
                </span>
                <span className="rd-broadcast-message">{b.message}</span>
                <span className="rd-broadcast-time">
                  {b.sent_at?.toDate ? b.sent_at.toDate().toLocaleTimeString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flag signal log */}
      <FlagSignalForm raceDayId={raceDay.id} />

      {flagDecisions.length > 0 && (
        <div className="rd-flag-log">
          <h3>Flag &amp; Course Decisions</h3>
          <div className="rd-broadcast-entries">
            {[...flagDecisions].reverse().map((d) => (
              <div key={d.id} className="rd-broadcast-entry">
                <span className={`rd-hub-decision-type rd-hub-dtype-${d.type}`}>
                  {d.flags?.join(', ') || d.type.replace(/_/g, ' ')}
                </span>
                <span className="rd-broadcast-message">{d.description}</span>
                <span className="rd-broadcast-time">
                  {d.made_at?.toDate ? d.made_at.toDate().toLocaleTimeString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function FinishPanel({ raceDay }) {
  const { user } = useAuth();
  const [sailInput, setSailInput] = useState('');
  const [selectedFleet, setSelectedFleet] = useState('');
  const [selectedRace, setSelectedRace] = useState(1);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editSail, setEditSail] = useState('');
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkSails, setBulkSails] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const inputRef = useRef(null);
  const editInputRef = useRef(null);

  // Derive fleets from existing fleet_starts
  const fleetStarts = raceDay.fleet_starts || [];
  const fleetIds = [...new Set(fleetStarts.map((fs) => fs.fleet_id))];
  const raceNumbers = [...new Set(fleetStarts.map((fs) => fs.race_number))].sort((a, b) => a - b);

  // Auto-select first fleet if only one
  useEffect(() => {
    if (!selectedFleet && fleetIds.length === 1) setSelectedFleet(fleetIds[0]);
  }, [fleetIds, selectedFleet]);

  useEffect(() => {
    if (!selectedRace && raceNumbers.length > 0) setSelectedRace(raceNumbers[0]);
  }, [raceNumbers, selectedRace]);

  // Focus edit input when editing
  useEffect(() => {
    if (editingId && editInputRef.current) editInputRef.current.focus();
  }, [editingId]);

  // Get active (non-superseded) finish records for current fleet/race
  const allFinishes = (raceDay.finish_records || []);
  const supersededIds = new Set(allFinishes.filter((f) => f.supersedes).map((f) => f.supersedes));
  const activeFinishes = allFinishes
    .filter((f) => !supersededIds.has(f.id))
    .filter((f) => !f.supersedes || !supersededIds.has(f.id));

  const currentFinishes = activeFinishes
    .filter((f) =>
      (!selectedFleet || f.fleet_id === selectedFleet) &&
      f.race_number === selectedRace
    )
    .sort((a, b) => {
      // Finished boats first by finish_time, then non-finished
      if (a.status === FINISH_STATUS.FINISHED && b.status !== FINISH_STATUS.FINISHED) return -1;
      if (a.status !== FINISH_STATUS.FINISHED && b.status === FINISH_STATUS.FINISHED) return 1;
      if (a.finish_time && b.finish_time) {
        const aTime = a.finish_time.seconds || 0;
        const bTime = b.finish_time.seconds || 0;
        return aTime - bTime;
      }
      return 0;
    });

  // Record a finish: sail number + timestamp
  const handleFinish = useCallback(async () => {
    const sail = sailInput.trim().toUpperCase();
    if (!sail || !user || saving) return;

    setSaving(true);
    try {
      const now = new Date();
      const entry = {
        id: crypto.randomUUID(),
        fleet_id: selectedFleet || 'default',
        race_number: selectedRace,
        sail_number: sail,
        finish_time: Timestamp.fromDate(now),
        status: FINISH_STATUS.FINISHED,
        position: currentFinishes.filter((f) => f.status === FINISH_STATUS.FINISHED).length + 1,
        recorded_by: user.uid,
        recorded_at: Timestamp.fromDate(now),
      };
      await addFinishRecord(raceDay.id, entry, user.uid);
      setSailInput('');
      inputRef.current?.focus();
    } catch (err) {
      reportWriteError('record finish', err, { raceDayId: raceDay.id, sailNumber: sail });
    } finally {
      setSaving(false);
    }
  }, [sailInput, user, saving, selectedFleet, selectedRace, currentFinishes, raceDay?.id]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFinish();
    }
  }, [handleFinish]);

  // Correct a finish record (supersedes pattern)
  const handleCorrect = useCallback(async (original) => {
    if (!editSail.trim() || !user) return;

    setSaving(true);
    try {
      const now = new Date();
      const corrected = {
        ...original,
        id: crypto.randomUUID(),
        sail_number: editSail.trim().toUpperCase(),
        supersedes: original.id,
        recorded_by: user.uid,
        recorded_at: Timestamp.fromDate(now),
      };
      // Remove Firestore timestamp objects, pass raw
      delete corrected.finish_time;
      corrected.finish_time = original.finish_time;
      await addFinishRecord(raceDay.id, corrected, user.uid, original);
      setEditingId(null);
      setEditSail('');
    } catch (err) {
      reportWriteError('correct finish', err, { raceDayId: raceDay.id, recordId: original.id });
    } finally {
      setSaving(false);
    }
  }, [editSail, user, raceDay?.id]);

  // Bulk mark boats with a status (DNF, DNS, OCS, etc.)
  const handleBulkMark = useCallback(async () => {
    const sails = bulkSails.split(/[,\s]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
    if (!sails.length || !bulkStatus || !user || bulkSaving) return;

    setBulkSaving(true);
    try {
      const now = new Date();
      for (const sail of sails) {
        const entry = {
          id: crypto.randomUUID(),
          fleet_id: selectedFleet || 'default',
          race_number: selectedRace,
          sail_number: sail,
          finish_time: Timestamp.fromDate(now),
          status: bulkStatus,
          recorded_by: user.uid,
          recorded_at: Timestamp.fromDate(now),
        };
        await addFinishRecord(raceDay.id, entry, user.uid);
      }
      setBulkSails('');
      setBulkStatus('');
    } catch (err) {
      reportWriteError('save finish records', err, { raceDayId: raceDay.id });
    } finally {
      setBulkSaving(false);
    }
  }, [bulkSails, bulkStatus, user, bulkSaving, selectedFleet, selectedRace, raceDay?.id]);

  const formatTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const statusLabels = {
    [FINISH_STATUS.FINISHED]: 'Finished',
    [FINISH_STATUS.DNF]: 'DNF',
    [FINISH_STATUS.DNS]: 'DNS',
    [FINISH_STATUS.DSQ]: 'DSQ',
    [FINISH_STATUS.OCS]: 'OCS',
    [FINISH_STATUS.RAF]: 'RAF',
    [FINISH_STATUS.RET]: 'RET',
  };

  return (
    <section className="rd-panel">
      <h2>Finish Line</h2>

      {/* Fleet / Race selector */}
      <div className="rd-finish-selectors">
        {fleetIds.length > 1 && (
          <label className="rd-finish-selector">
            Fleet:
            <select value={selectedFleet} onChange={(e) => setSelectedFleet(e.target.value)}>
              <option value="">All fleets</option>
              {fleetIds.map((fid) => (
                <option key={fid} value={fid}>{fid}</option>
              ))}
            </select>
          </label>
        )}
        {raceNumbers.length > 1 && (
          <label className="rd-finish-selector">
            Race:
            <select value={selectedRace} onChange={(e) => setSelectedRace(Number(e.target.value))}>
              {raceNumbers.map((rn) => (
                <option key={rn} value={rn}>Race {rn}</option>
              ))}
            </select>
          </label>
        )}
        {fleetIds.length === 0 && (
          <p className="rd-finish-hint">No fleet starts recorded yet. Start a fleet on the Starts tab first.</p>
        )}
      </div>

      {/* Quick finish entry — keyboard-first */}
      <div className="rd-finish-entry">
        <label className="rd-finish-entry-label">Record Finish:</label>
        <div className="rd-finish-entry-row">
          <input
            ref={inputRef}
            type="text"
            className="rd-finish-sail-input"
            placeholder="Sail number"
            value={sailInput}
            onChange={(e) => setSailInput(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            disabled={saving}
          />
          <button
            className="btn btn-primary"
            onClick={handleFinish}
            disabled={!sailInput.trim() || saving}
          >
            {saving ? 'Recording...' : 'Finish (Enter)'}
          </button>
        </div>
        <p className="rd-finish-entry-hint">Type sail number and press Enter to record with current timestamp</p>
      </div>

      {/* Running order table */}
      <div className="rd-finish-table-wrap">
        <table className="rd-finish-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Sail</th>
              <th>Time</th>
              <th>Status</th>
              <th>Fleet</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {currentFinishes.length === 0 && (
              <tr><td colSpan={6} className="rd-finish-empty">No finishes recorded yet</td></tr>
            )}
            {currentFinishes.map((f, i) => (
              <tr key={f.id} className={f.status !== FINISH_STATUS.FINISHED ? 'rd-finish-row-status' : ''}>
                <td>{f.status === FINISH_STATUS.FINISHED ? f.position || (i + 1) : '—'}</td>
                <td>
                  {editingId === f.id ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      className="rd-finish-edit-input"
                      value={editSail}
                      onChange={(e) => setEditSail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); handleCorrect(f); }
                        if (e.key === 'Escape') { setEditingId(null); setEditSail(''); }
                      }}
                    />
                  ) : (
                    <strong>{f.sail_number}</strong>
                  )}
                </td>
                <td>{formatTime(f.finish_time)}</td>
                <td><span className={`rd-finish-status rd-finish-status-${f.status}`}>{statusLabels[f.status] || f.status}</span></td>
                <td>{f.fleet_id}</td>
                <td>
                  {editingId === f.id ? (
                    <span className="rd-finish-edit-actions">
                      <button className="btn btn-sm btn-success" onClick={() => handleCorrect(f)} disabled={saving}>Save</button>
                      <button className="btn btn-sm" onClick={() => { setEditingId(null); setEditSail(''); }}>Cancel</button>
                    </span>
                  ) : (
                    <button
                      className="btn btn-sm rd-finish-edit-btn"
                      onClick={() => { setEditingId(f.id); setEditSail(f.sail_number); }}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bulk status marking */}
      <div className="rd-finish-bulk">
        <h3>Bulk Status Marking</h3>
        <div className="rd-finish-bulk-row">
          <select
            className="rd-finish-bulk-select"
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
          >
            <option value="">Select status...</option>
            <option value={FINISH_STATUS.DNF}>DNF — Did Not Finish</option>
            <option value={FINISH_STATUS.DNS}>DNS — Did Not Start</option>
            <option value={FINISH_STATUS.OCS}>OCS — On Course Side</option>
            <option value={FINISH_STATUS.DSQ}>DSQ — Disqualified</option>
            <option value={FINISH_STATUS.RAF}>RAF — Retired After Finish</option>
            <option value={FINISH_STATUS.RET}>RET — Retired</option>
          </select>
          <input
            type="text"
            className="rd-finish-bulk-input"
            placeholder="Sail numbers (comma or space separated)"
            value={bulkSails}
            onChange={(e) => setBulkSails(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleBulkMark(); } }}
          />
          <button
            className="btn btn-warning"
            onClick={handleBulkMark}
            disabled={!bulkStatus || !bulkSails.trim() || bulkSaving}
          >
            {bulkSaving ? 'Marking...' : 'Mark'}
          </button>
        </div>
      </div>

      {/* Summary footer */}
      <div className="rd-finish-summary">
        <span>{currentFinishes.filter((f) => f.status === FINISH_STATUS.FINISHED).length} finished</span>
        {currentFinishes.filter((f) => f.status !== FINISH_STATUS.FINISHED).length > 0 && (
          <span> · {currentFinishes.filter((f) => f.status !== FINISH_STATUS.FINISHED).length} other status</span>
        )}
      </div>
    </section>
  );
}

function AwarenessPanel({ raceDay }) {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [noticeMsg, setNoticeMsg] = useState('');
  const [noticeChannel, setNoticeChannel] = useState(BROADCAST_CHANNEL.APP);
  const [saving, setSaving] = useState(false);
  const noticeInputRef = useRef(null);

  // Load today's race schedule for this club/event
  useEffect(() => {
    if (!raceDay?.clubId || !raceDay?.event_id || !raceDay?.date) {
      setScheduleLoading(false);
      return;
    }
    const path = `clubs/${raceDay.clubId}/events/${raceDay.event_id}/race_schedule/${raceDay.date}`;
    getDoc(doc(db, path))
      .then((snap) => {
        if (snap.exists()) {
          setSchedule(snap.data());
        }
      })
      .catch((err) => console.error('Failed to load race schedule:', err))
      .finally(() => setScheduleLoading(false));
  }, [raceDay?.clubId, raceDay?.event_id, raceDay?.date]);

  // Active broadcasts (non-superseded)
  const allBroadcasts = raceDay.broadcasts || [];
  const supersededIds = new Set(allBroadcasts.filter((b) => b.supersedes).map((b) => b.supersedes));
  const activeBroadcasts = allBroadcasts.filter((b) => !supersededIds.has(b.id));

  // Parse schedule locations
  const locations = schedule?.locations || {};
  const locationEntries = Object.entries(locations);

  // Identify which fleets are part of the current race day's fleet_starts
  const currentFleetIds = new Set((raceDay.fleet_starts || []).map((fs) => fs.fleet_id));

  const handleAddNotice = useCallback(async () => {
    const msg = noticeMsg.trim();
    if (!msg || !user || saving) return;

    setSaving(true);
    try {
      const now = new Date();
      const entry = {
        id: crypto.randomUUID(),
        channel: noticeChannel,
        message: msg,
        sent_at: Timestamp.fromDate(now),
        sent_by: user.uid,
      };
      await addBroadcast(raceDay.id, entry, user.uid);
      setNoticeMsg('');
      noticeInputRef.current?.focus();
    } catch (err) {
      reportWriteError('add notice', err, { raceDayId: raceDay.id });
    } finally {
      setSaving(false);
    }
  }, [noticeMsg, noticeChannel, user, saving, raceDay?.id]);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  const channelLabels = {
    [BROADCAST_CHANNEL.VHF]: 'VHF',
    [BROADCAST_CHANNEL.FLAG]: 'Flag',
    [BROADCAST_CHANNEL.SOUND]: 'Sound',
    [BROADCAST_CHANNEL.APP]: 'App',
  };

  return (
    <section className="rd-panel">
      <h2>Area Awareness</h2>

      {/* Today's other races from schedule */}
      <div className="rd-awareness-schedule">
        <h3>Today&rsquo;s Racing Schedule</h3>
        {scheduleLoading && <p className="rd-awareness-loading">Loading schedule...</p>}
        {!scheduleLoading && locationEntries.length === 0 && (
          <p className="rd-awareness-empty">No race schedule published for today.</p>
        )}
        {!scheduleLoading && locationEntries.length > 0 && (
          <div className="rd-awareness-locations">
            {locationEntries.map(([locId, loc]) => {
              const isCurrentArea = (loc.fleet && currentFleetIds.has(loc.fleet));
              return (
                <div key={locId} className={`rd-awareness-location${isCurrentArea ? ' rd-awareness-location-current' : ''}`}>
                  <div className="rd-awareness-loc-header">
                    <strong>{loc.name || locId}</strong>
                    {loc.vhf && <span className="rd-awareness-vhf">VHF Ch {loc.vhf}</span>}
                    {isCurrentArea && <span className="rd-awareness-current-badge">Your area</span>}
                  </div>
                  {(loc.starts || []).length > 0 && (
                    <table className="rd-awareness-starts-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Classes</th>
                          <th>Flags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(loc.starts || []).map((s, i) => (
                          <tr key={i}>
                            <td className="rd-awareness-time">{s.time}</td>
                            <td>{(s.classes || []).map((c) =>
                              c.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                            ).join(', ')}</td>
                            <td>{(s.flags || []).join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Area notices */}
      <div className="rd-awareness-notices">
        <h3>Area Notices</h3>

        {/* Create notice */}
        <div className="rd-awareness-notice-form">
          <div className="rd-awareness-notice-row">
            <select
              className="rd-awareness-channel-select"
              value={noticeChannel}
              onChange={(e) => setNoticeChannel(e.target.value)}
            >
              <option value={BROADCAST_CHANNEL.APP}>App Notice</option>
              <option value={BROADCAST_CHANNEL.VHF}>VHF Broadcast</option>
              <option value={BROADCAST_CHANNEL.FLAG}>Flag Signal</option>
              <option value={BROADCAST_CHANNEL.SOUND}>Sound Signal</option>
            </select>
            <input
              ref={noticeInputRef}
              type="text"
              className="rd-awareness-notice-input"
              placeholder="Area notice (e.g. 'Mark laying in progress NW of harbour')"
              value={noticeMsg}
              onChange={(e) => setNoticeMsg(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNotice(); } }}
              disabled={saving}
            />
            <button
              className="btn btn-primary"
              onClick={handleAddNotice}
              disabled={!noticeMsg.trim() || saving}
            >
              {saving ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>

        {/* Notice list */}
        {activeBroadcasts.length === 0 && (
          <p className="rd-awareness-empty">No area notices posted yet.</p>
        )}
        {activeBroadcasts.length > 0 && (
          <div className="rd-awareness-notice-list">
            {[...activeBroadcasts].reverse().map((b) => (
              <div key={b.id} className={`rd-awareness-notice rd-awareness-notice-${b.channel}`}>
                <div className="rd-awareness-notice-header">
                  <span className={`rd-awareness-channel-badge rd-awareness-ch-${b.channel}`}>
                    {channelLabels[b.channel] || b.channel}
                  </span>
                  <span className="rd-awareness-notice-time">{formatTime(b.sent_at)}</span>
                </div>
                <p className="rd-awareness-notice-msg">{b.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Results Panel ────────────────────────────────────────────────────

const STATUS_LABELS = {
  [FINISH_STATUS.FINISHED]: 'Finished',
  [FINISH_STATUS.DNF]: 'DNF',
  [FINISH_STATUS.DNS]: 'DNS',
  [FINISH_STATUS.DSQ]: 'DSQ',
  [FINISH_STATUS.OCS]: 'OCS',
  [FINISH_STATUS.RAF]: 'RAF',
  [FINISH_STATUS.RET]: 'RET',
};

function formatElapsed(seconds) {
  if (seconds == null) return '--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatFinishTime(ts) {
  if (!ts) return '--';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString();
}

function ResultsTable({ records, raceNumber, raceDayId }) {
  const { user } = useAuth();
  const [correcting, setCorrecting] = useState(null); // id being corrected
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);

  // Filter to this race, exclude superseded
  const supersededIds = new Set(records.filter((r) => r.supersedes).map((r) => r.supersedes));
  const active = records.filter(
    (r) => r.race_number === raceNumber && !supersededIds.has(r.id) && !r.supersedes
  );
  // Also include correction entries (have supersedes) for this race
  const corrections = records.filter(
    (r) => r.race_number === raceNumber && r.supersedes
  );
  // Merge: for each sail number, prefer the correction over the original
  const correctionMap = new Map();
  for (const c of corrections) {
    correctionMap.set(c.supersedes, c);
  }
  const merged = active.map((r) => correctionMap.get(r.id) || r);

  // Sort by position, then finished status, then sail number
  const sorted = [...merged].sort((a, b) => {
    if (a.status === FINISH_STATUS.FINISHED && b.status !== FINISH_STATUS.FINISHED) return -1;
    if (a.status !== FINISH_STATUS.FINISHED && b.status === FINISH_STATUS.FINISHED) return 1;
    if (a.position != null && b.position != null) return a.position - b.position;
    if (a.position != null) return -1;
    if (b.position != null) return 1;
    return (a.sail_number || '').localeCompare(b.sail_number || '');
  });

  const handleCorrect = useCallback(async (record) => {
    if (!user || saving || !newStatus) return;
    setSaving(true);
    try {
      await addFinishRecord(raceDayId, {
        id: crypto.randomUUID(),
        fleet_id: record.fleet_id,
        race_number: record.race_number,
        sail_number: record.sail_number,
        boat_name: record.boat_name || undefined,
        finish_time: record.finish_time,
        elapsed_seconds: record.elapsed_seconds,
        corrected_seconds: record.corrected_seconds,
        status: newStatus,
        position: newStatus === FINISH_STATUS.FINISHED ? record.position : undefined,
        recorded_by: user.uid,
        recorded_at: new Date(),
        supersedes: record.id,
      }, user.uid, record);
      setCorrecting(null);
      setNewStatus('');
    } catch (err) {
      reportWriteError('correct result status', err, { raceDayId, recordId: record.id });
    } finally {
      setSaving(false);
    }
  }, [raceDayId, user, saving, newStatus]);

  if (sorted.length === 0) {
    return <p className="rd-results-empty">No finish records for Race {raceNumber}.</p>;
  }

  return (
    <div className="rd-results-table-wrap">
      <table className="rd-results-table">
        <thead>
          <tr>
            <th>Pos</th>
            <th>Sail</th>
            <th>Boat</th>
            <th>Finish Time</th>
            <th>Elapsed</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, idx) => (
            <tr key={r.id} className={r.status !== FINISH_STATUS.FINISHED ? 'rd-results-row-nf' : ''}>
              <td className="rd-results-pos">
                {r.status === FINISH_STATUS.FINISHED ? (r.position ?? idx + 1) : '--'}
              </td>
              <td className="rd-results-sail">{r.sail_number}</td>
              <td>{r.boat_name || ''}</td>
              <td className="rd-results-mono">{formatFinishTime(r.finish_time)}</td>
              <td className="rd-results-mono">{formatElapsed(r.elapsed_seconds)}</td>
              <td>
                <span className={`rd-results-status rd-rstatus-${r.status}`}>
                  {STATUS_LABELS[r.status] || r.status}
                </span>
              </td>
              <td>
                {correcting === r.id ? (
                  <div className="rd-results-correct-row">
                    <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="rd-results-select">
                      <option value="">Change to...</option>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                    <button className="btn btn-sm btn-primary" disabled={!newStatus || saving} onClick={() => handleCorrect(r)}>
                      Save
                    </button>
                    <button className="btn btn-sm" onClick={() => { setCorrecting(null); setNewStatus(''); }}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button className="btn btn-sm rd-results-correct-btn" onClick={() => { setCorrecting(r.id); setNewStatus(''); }}>
                    Correct
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultsPanel({ raceDay }) {
  const { user } = useAuth();
  const [publishing, setPublishing] = useState(false);

  const finishRecords = raceDay.finish_records || [];
  const raceNumbers = [...new Set(finishRecords.map((r) => r.race_number))].sort((a, b) => a - b);
  const [selectedRace, setSelectedRace] = useState(raceNumbers[0] || 1);

  const isCompleted = raceDay.status === RACE_DAY_STATUS.COMPLETED;

  const handlePublish = useCallback(async () => {
    if (!user || publishing) return;
    if (!confirm('Mark this race day as completed in the committee record?')) return;
    setPublishing(true);
    try {
      await updateRaceDayStatus(raceDay.id, RACE_DAY_STATUS.COMPLETED, user.uid);
    } catch (err) {
      reportWriteError('mark race day complete', err, { raceDayId: raceDay.id });
    } finally {
      setPublishing(false);
    }
  }, [raceDay?.id, user, publishing]);

  return (
    <section className="rd-panel rd-results-panel">
      <div className="rd-results-header">
        <h2>Results</h2>
        <div className="rd-results-header-right">
          {raceNumbers.length > 1 && (
            <select value={selectedRace} onChange={(e) => setSelectedRace(Number(e.target.value))} className="rd-results-race-select">
              {raceNumbers.map((n) => <option key={n} value={n}>Race {n}</option>)}
            </select>
          )}
          {!isCompleted && (
            <button className="btn btn-primary" disabled={publishing || finishRecords.length === 0} onClick={handlePublish}>
              {publishing ? 'Saving...' : 'Complete Race Day (committee record only)'}
            </button>
          )}
          {isCompleted && (
            <span className="rd-results-published">Complete</span>
          )}
        </div>
      </div>

      {isCompleted && (
        <div className="rd-results-notice">
          Race day marked complete in the committee record. This does not publish results to app users.
        </div>
      )}

      <ResultsTable
        records={finishRecords}
        raceNumber={selectedRace}
        raceDayId={raceDay.id}
      />

      {/* Summary stats */}
      {finishRecords.length > 0 && (
        <div className="rd-results-summary">
          <span>{finishRecords.filter((r) => r.status === FINISH_STATUS.FINISHED && !r.supersedes).length} finished</span>
          <span>{finishRecords.filter((r) => r.status === FINISH_STATUS.DNF && !r.supersedes).length} DNF</span>
          <span>{finishRecords.filter((r) => r.status === FINISH_STATUS.DNS && !r.supersedes).length} DNS</span>
          <span>{finishRecords.filter((r) => [FINISH_STATUS.DSQ, FINISH_STATUS.OCS].includes(r.status) && !r.supersedes).length} DSQ/OCS</span>
          <span>{finishRecords.filter((r) => r.status === FINISH_STATUS.RET && !r.supersedes).length} RET</span>
        </div>
      )}
    </section>
  );
}

const TABS = [
  { key: 'hub', label: 'Hub', Component: HubPanel },
  { key: 'briefing', label: 'Briefing', Component: BriefingPanel },
  { key: 'starts', label: 'Starts', Component: StartsPanel },
  { key: 'course', label: 'Course', Component: CoursePanel },
  { key: 'finish', label: 'Finish', Component: FinishPanel },
  { key: 'awareness', label: 'Awareness', Component: AwarenessPanel },
  { key: 'results', label: 'Results', Component: ResultsPanel },
];

// ── Share button with copy-link + QR ───────────────────────────────

function ShareButton() {
  const [showPopover, setShowPopover] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = window.location.href;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  // Generate QR code via qrserver.com (Google Charts QR API is shut down)
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="rd-share-wrapper">
      <button
        className="btn btn-primary rd-share-btn"
        onClick={() => setShowPopover((v) => !v)}
      >
        Share
      </button>
      {showPopover && (
        <div className="rd-share-popover">
          <div className="rd-share-popover-header">
            <strong>Share this Race Day</strong>
            <button
              className="rd-share-close"
              onClick={() => setShowPopover(false)}
            >
              &times;
            </button>
          </div>
          <div className="rd-share-link-row">
            <input
              type="text"
              readOnly
              value={shareUrl}
              className="rd-share-url"
              onClick={(e) => e.target.select()}
            />
            <button className="btn btn-sm" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="rd-share-qr">
            <img src={qrUrl} alt="QR Code" width={180} height={180} />
          </div>
          <p className="rd-share-hint">
            Share this link or scan the QR code to access this race day.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Status badge ───────────────────────────────────────────────────

function StatusBadge({ status }) {
  return <span className={`rd-status rd-status-${status}`}>{status}</span>;
}

// ── Race Day List (no raceDayId) ───────────────────────────────────

function RaceDayList({ clubId }) {
  const [raceDays, setRaceDays] = useState(null);
  const [error, setError] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [scheduledDates, setScheduledDates] = useState([]);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    return subscribeToClubRaceDays(
      clubId,
      setRaceDays,
      (err) => setError(err.message),
    );
  }, [clubId]);

  // Load seasons for the "Create" flow
  useEffect(() => {
    loadSeasons(clubId).then((s) => {
      s.sort((a, b) => (b.id > a.id ? 1 : -1));
      setSeasons(s);
      if (s.length) setSelectedEvent(s[0].id);
    }).catch(() => {});
  }, [clubId]);

  // Load published schedule dates for the selected event
  useEffect(() => {
    if (!selectedEvent) { setScheduledDates([]); return; }
    const indexPath = `clubs/${clubId}/events/${selectedEvent}/race_schedule/_index`;
    getDoc(doc(db, indexPath)).then((snap) => {
      if (snap.exists()) {
        setScheduledDates(snap.data().dates || []);
      } else {
        setScheduledDates([]);
      }
    }).catch(() => setScheduledDates([]));
  }, [clubId, selectedEvent]);

  const today = todayISO();
  const upcomingScheduled = scheduledDates.filter((d) => {
    const diff = daysFromNow(d);
    return diff >= 0 && diff <= 7;
  });
  const existingDates = new Set((raceDays || []).map((rd) => rd.date));

  const handleCreate = useCallback(async (date) => {
    if (!user || !selectedEvent || creating) return;
    setCreating(true);
    try {
      const newId = await createRaceDay({
        clubId,
        eventId: selectedEvent,
        date,
        uid: user.uid,
      });
      navigate(`/clubs/${clubId}/race-day/${newId}`);
    } catch (err) {
      reportWriteError('create race day', err, { clubId, eventId: selectedEvent, date });
      setError(err.message);
      setCreating(false);
    }
  }, [user, selectedEvent, creating, clubId, navigate]);

  if (error) return <div className="page"><div className="error-banner">{error}</div></div>;
  if (!raceDays) return <div className="page"><p>Loading race days...</p></div>;

  // Split into today/active and past
  const todayAndActive = raceDays.filter((rd) =>
    rd.date === today || rd.status === RACE_DAY_STATUS.RACING
  );
  const upcoming = raceDays.filter((rd) => {
    const diff = daysFromNow(rd.date);
    return diff > 0 && diff <= 7 && rd.status !== RACE_DAY_STATUS.RACING;
  });
  const past = raceDays.filter((rd) => {
    const diff = daysFromNow(rd.date);
    return diff < 0 && rd.status !== RACE_DAY_STATUS.RACING;
  });

  return (
    <>
      <header className="rd-top-bar">
        <div className="rd-top-left">
          <Link to={`/clubs/${clubId}`} className="rd-back-link">&larr; Dashboard</Link>
          <strong className="rd-top-title">Race Days</strong>
        </div>
      </header>
      <div className="page">
        {/* Create New Race Day */}
        <div className="rd-create-section">
          <h3>Create New Race Day</h3>
          <div className="rd-create-row">
            {seasons.length > 1 && (
              <select
                className="rd-create-select"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
              >
                <option value="">Select season...</option>
                {seasons.map((s) => (
                  <option key={s.id} value={s.id}>{s.name || s.id}</option>
                ))}
              </select>
            )}
            {seasons.length === 1 && (
              <span className="rd-create-event-name">{seasons[0].name || seasons[0].id}</span>
            )}
            <button
              className="btn btn-primary"
              disabled={!selectedEvent || creating}
              onClick={() => handleCreate(today)}
            >
              {creating ? 'Creating...' : `Create for Today (${formatDate(today)})`}
            </button>
          </div>

          {/* Upcoming scheduled dates from race_schedule that don't have a race day yet */}
          {upcomingScheduled.length > 0 && (
            <div className="rd-upcoming-schedule">
              <h4>Upcoming Scheduled Dates</h4>
              <div className="rd-schedule-chips">
                {upcomingScheduled.map((d) => (
                  <div key={d} className="rd-schedule-chip">
                    <span>{formatDate(d)}</span>
                    {existingDates.has(d) ? (
                      <span className="rd-chip-exists">Race day exists</span>
                    ) : (
                      <button
                        className="btn btn-sm"
                        disabled={!selectedEvent || creating}
                        onClick={() => handleCreate(d)}
                      >
                        Create
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Today / Active */}
        {todayAndActive.length > 0 && (
          <>
            <h3 className="rd-section-heading">Today / Active</h3>
            <div className="rd-day-list">
              {todayAndActive.map((rd) => (
                <button
                  key={rd.id}
                  className="rd-day-card rd-day-card-active"
                  onClick={() => navigate(`/clubs/${clubId}/race-day/${rd.id}`)}
                >
                  <div className="rd-day-card-left">
                    <span className="rd-day-date">{formatDate(rd.date)}</span>
                    <span className="rd-day-event">{rd.event_id}</span>
                  </div>
                  <StatusBadge status={rd.status} />
                </button>
              ))}
            </div>
          </>
        )}

        {/* Upcoming */}
        {upcoming.length > 0 && (
          <>
            <h3 className="rd-section-heading">Upcoming</h3>
            <div className="rd-day-list">
              {upcoming.map((rd) => (
                <button
                  key={rd.id}
                  className="rd-day-card"
                  onClick={() => navigate(`/clubs/${clubId}/race-day/${rd.id}`)}
                >
                  <div className="rd-day-card-left">
                    <span className="rd-day-date">{formatDate(rd.date)}</span>
                    <span className="rd-day-event">{rd.event_id}</span>
                  </div>
                  <StatusBadge status={rd.status} />
                </button>
              ))}
            </div>
          </>
        )}

        {/* Past */}
        {past.length > 0 && (
          <>
            <h3 className="rd-section-heading">Past</h3>
            <div className="rd-day-list">
              {past.map((rd) => (
                <button
                  key={rd.id}
                  className="rd-day-card"
                  onClick={() => navigate(`/clubs/${clubId}/race-day/${rd.id}`)}
                >
                  <div className="rd-day-card-left">
                    <span className="rd-day-date">{formatDate(rd.date)}</span>
                    <span className="rd-day-event">{rd.event_id}</span>
                  </div>
                  <StatusBadge status={rd.status} />
                </button>
              ))}
            </div>
          </>
        )}

        {raceDays.length === 0 && upcomingScheduled.length === 0 && (
          <p className="notice">No race days yet. Create one above to get started.</p>
        )}
      </div>
    </>
  );
}

// ── Main layout (with raceDayId) ───────────────────────────────────

function RaceDayDetail({ clubId, raceDayId }) {
  const [raceDay, setRaceDay] = useState(undefined); // undefined = loading
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('hub');

  useEffect(() => {
    return subscribeToRaceDay(
      raceDayId,
      setRaceDay,
      (err) => setError(err.message),
    );
  }, [raceDayId]);

  if (error) return <div className="page"><div className="error-banner">{error}</div></div>;
  if (raceDay === undefined) return <div className="page"><p>Loading...</p></div>;
  if (raceDay === null) return <div className="page"><div className="error-banner">Race day not found.</div></div>;

  const ActiveComponent = TABS.find((t) => t.key === activeTab)?.Component ?? HubPanel;

  return (
    <>
      <header className="rd-top-bar">
        <div className="rd-top-left">
          <Link to={`/clubs/${clubId}/race-day`} className="rd-back-link">&larr; Race Days</Link>
          <strong className="rd-top-title">{raceDay.date}</strong>
          <StatusBadge status={raceDay.status} />
        </div>
        <div className="rd-top-right">
          <ShareButton />
        </div>
      </header>
      <nav className="rd-tab-bar">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`rd-tab${activeTab === tab.key ? ' rd-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      <div className="page rd-content">
        <ActiveComponent raceDay={raceDay} />
      </div>
    </>
  );
}

// ── Exported layout component ──────────────────────────────────────

export function RaceDayWriteErrorBanner({ error, onDismiss }) {
  if (!error) return null;
  return (
    <div className="rd-write-error" role="alert" aria-live="assertive">
      <span>{error}</span>
      <button type="button" className="btn btn-sm" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

export default function RaceDayLayout() {
  const { clubId, raceDayId } = useParams();
  const { user } = useAuth();
  const [writeError, setWriteError] = useState('');

  useEffect(() => {
    setWriteError('');
  }, [raceDayId]);

  useEffect(() => {
    const handleWriteError = (event) => setWriteError(event.detail);
    window.addEventListener('race-day-write-error', handleWriteError);
    return () => window.removeEventListener('race-day-write-error', handleWriteError);
  }, []);

  if (!user) return null;

  return (
    <>
      <RaceDayWriteErrorBanner error={writeError} onDismiss={() => setWriteError('')} />
      {!raceDayId
        ? <RaceDayList clubId={clubId} />
        : <RaceDayDetail clubId={clubId} raceDayId={raceDayId} />}
    </>
  );
}
